// His offline saves (Z Furniture `write` / `flush`): at the client's there is often no network. A
// link, an arrival or a release that cannot reach the server is kept on this phone and sent again
// when the network comes back, and every 30 seconds; the server keeps a newer version if one was
// saved meanwhile (the action carries the moment it was pressed). A refusal drops it with its reason.
import { sendCartAction, type CartAction } from "@/services/carts";

export type PendingSave = { number: number; action: CartAction; body: Record<string, unknown> };
const KEY = "myotaku-warehouse:cart-queue";

export function pendingSaves(): PendingSave[] {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]") as PendingSave[]; } catch { return []; }
}
function keep(list: PendingSave[]) {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* the phone's storage refused: nothing more to do */ }
  window.dispatchEvent(new Event("cart-queue"));
}
// No answer at all — no network, or the server unreachable — as opposed to the server saying no.
const unreachable = (e: unknown) => e instanceof TypeError || (typeof navigator !== "undefined" && navigator.onLine === false);

export async function saveCart(p: PendingSave): Promise<"sent" | "queued"> {
  const body = { ...p.body, at: new Date().toISOString() };
  try { await sendCartAction(p.number, p.action, body); return "sent"; } catch (e) {
    if (!unreachable(e)) throw e;
    keep([...pendingSaves(), { ...p, body }]);
    return "queued";
  }
}

let flushing = false;
// Sends what waits, oldest first; stops at the first that still cannot get through. Returns the
// refusals, to show their reason.
export async function flushCarts(): Promise<string[]> {
  if (flushing) return [];
  flushing = true;
  const refused: string[] = [];
  try {
    for (const p of pendingSaves()) {
      try { await sendCartAction(p.number, p.action, p.body); } catch (e) {
        if (unreachable(e)) break;
        refused.push((e as Error).message);
      }
      keep(pendingSaves().slice(1));
    }
  } finally { flushing = false; }
  return refused;
}
